import { ReactNode, createContext, useEffect, useState } from "react";
import { io, Socket } from "socket.io-client";
import { useSelector } from "react-redux";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import * as Sentry from "@sentry/capacitor";
import { getApiRoot } from "./helpers/apiRequest";
import { appVersion, isMobile } from "./helpers/mobile";
import { selectStatus } from "./ducks/auth/selectors";
import useFeature from "./hooks/useFeature";

export const SocketContext = createContext<Socket | null>(null);

// Establishes the (single) socket.io connection to the server. The driver app
// uses it as a lightweight "your data is stale, refetch" channel — see
// RoutePage, which joins a per-route room and refetches on the
// `driver-route.version-changed` event instead of relying only on polling.
const SocketConnection = ({ children }: { children?: ReactNode }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  // The socket handshake captures the Express session cookie at connect time,
  // and the server refuses a handshake without a logged-in session. So only
  // connect while logged in, and re-establish the connection whenever that
  // changes.
  const authenticated = useSelector(selectStatus) === "AUTHENTICATED";
  // Open the socket only for orgs that use it for at least one live-update
  // feature; otherwise it would sit idle (and emit connect_error noise).
  const routeUpdatesViaWebsockets = useFeature(
    "mobile-app-route-updates-websockets"
  );
  const invoiceLinesViaWebsockets = useFeature(
    "mobile-app-invoice-lines-websockets"
  );
  const enabled =
    authenticated && (routeUpdatesViaWebsockets || invoiceLinesViaWebsockets);

  useEffect(() => {
    if (!enabled) {
      setSocket(null);
      return;
    }
    // Absolute origin on native (cross-origin → withCredentials sends the
    // session cookie); "" on web, where we connect same-origin (proxied to the
    // server in dev by vite.config, served directly in production).
    const url = getApiRoot() || "/";
    const connection = io(url, {
      path: "/socket",
      // Start on HTTP long-polling, then auto-upgrade to a real WebSocket when
      // available. This is socket.io's robust default: polling proxies as a
      // plain request, and on carrier proxies / captive networks that block the
      // WS upgrade it simply stays on polling instead of failing. (Listing
      // websocket first would attempt a raw WS with no fallback, since
      // tryAllTransports defaults to false.)
      transports: ["polling", "websocket"],
      withCredentials: true,
      // On native, CapacitorHttp sends the polling requests through native
      // HTTP, which adds no Origin header. The server accepts the native app
      // by this header instead (see isSocketRequestAllowed on the server).
      extraHeaders: isMobile
        ? {
            "x-app-version": appVersion ?? "",
            "x-app-platform": Capacitor.getPlatform(),
          }
        : undefined,
      reconnectionDelayMax: 10000,
    });
    setSocket(connection);

    // With reconnection on, a failed handshake otherwise retries silently —
    // surface why (auth rejected / origin rejected / network) while we're still
    // confirming the cookie reaches the handshake on real devices. Network
    // failures retry up to every 10s, so report only the first failure until
    // the socket connects again.
    let connectErrorReported = false;
    const onConnectError = (err: Error) => {
      console.warn("Socket connect_error", err.message);
      if (connectErrorReported) {
        return;
      }
      connectErrorReported = true;
      Sentry.captureMessage(`Socket connect_error: ${err.message}`);
    };
    connection.on("connect_error", onConnectError);
    const onConnect = () => {
      connectErrorReported = false;
    };
    connection.on("connect", onConnect);

    // The server drops a socket whose handshake carried no driver session
    // before letting it join a room. socket.io doesn't auto-reconnect after a
    // server-side disconnect, so re-handshake to pick up the current session
    // cookie. Throttled: the `connect` handlers re-join rooms, so a genuinely
    // dead session would otherwise loop join → disconnect → reconnect.
    let lastForcedReconnectAt = 0;
    const onDisconnect = (reason: Socket.DisconnectReason) => {
      if (reason !== "io server disconnect") {
        return;
      }
      if (Date.now() - lastForcedReconnectAt < 30_000) {
        return;
      }
      lastForcedReconnectAt = Date.now();
      connection.connect();
    };
    connection.on("disconnect", onDisconnect);

    // The OS freezes/kills the WebView (and its socket) while the app is
    // backgrounded, and socket.io's reconnection timers are frozen with it. On
    // resume, force an immediate reconnect rather than waiting for backoff to
    // notice the dead connection; the `connect` handler then re-joins rooms and
    // refetches.
    const stateListener = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive && !connection.connected) {
        connection.connect();
      }
    });

    return () => {
      connection.off("connect_error", onConnectError);
      connection.off("connect", onConnect);
      connection.off("disconnect", onDisconnect);
      void stateListener.then((l) => l.remove());
      connection.close();
    };
  }, [enabled]);

  return (
    <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>
  );
};

export default SocketConnection;
